import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Loader2,
  MoreVertical,
  NotepadText,
  Play,
  RotateCcw,
  Save,
  ShieldX,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Progress } from '@/app/components/ui/progress';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { ZonePicker } from '@/components/shared/zone-picker';
import { SpotPicker } from '@/components/shared/spot-picker';
import { useWorkTask, useWorkSteps, useUpdateWorkStep, useUpdateWorkTask } from '@/hooks/use-work';
import { useProject } from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { WorkStep, WorkTask } from '@/lib/types';

const COMPLETED_RESULTS = new Set<WorkStep['result']>(['pass', 'fail', 'done', 'n_a']);

function taskStatusLabel(status: WorkTask['status']) {
  switch (status) {
    case 'todo':
      return 'To Do';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'blocked':
      return 'Blocked';
  }
}

function resultBadge(result: WorkStep['result']) {
  switch (result) {
    case 'pass':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <Check className="h-3 w-3 mr-1" />
          Pass
        </Badge>
      );
    case 'fail':
      return (
        <Badge variant="destructive">
          <X className="h-3 w-3 mr-1" />
          Fail
        </Badge>
      );
    case 'done':
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Check className="h-3 w-3 mr-1" />
          Done
        </Badge>
      );
    case 'n_a':
      return (
        <Badge variant="outline" className="text-gray-600 dark:text-muted-foreground">
          N/A
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-gray-500 dark:text-muted-foreground">
          Pending
        </Badge>
      );
  }
}

function StepNotes({
  step,
  onSave,
  isPending,
}: {
  step: WorkStep;
  onSave: (notes: string) => void;
  isPending: boolean;
}) {
  const [value, setValue] = useState(step.notes || '');
  const dirty = value !== (step.notes || '');

  const handleSave = () => {
    onSave(value);
  };

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-muted-foreground">
        <NotepadText className="h-4 w-4" />
        <span>Notes</span>
      </div>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add field notes for this step..."
        rows={2}
        className="resize-none"
      />
      {dirty && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleSave}
          disabled={isPending}
          className="h-9"
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          {isPending ? 'Saving...' : 'Save note'}
        </Button>
      )}
    </div>
  );
}

interface StepLocationPickersProps {
  step: WorkStep;
  projectLocationId?: number;
  canEditLocation: boolean;
  onUpdate: (stepId: number, data: { result?: WorkStep['result']; notes?: string; zone?: number | null; spot?: number | null }) => void;
  isPending: boolean;
}

function StepLocationPickers({ step, projectLocationId, canEditLocation, onUpdate, isPending }: StepLocationPickersProps) {
  if (!canEditLocation) {
    if (!step.zone_path && !step.spot_name) return null;
    return (
      <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-muted-foreground">
        {step.zone_path && <span>Zone: {step.zone_path}</span>}
        {step.spot_name && <span>Spot: {step.spot_name}</span>}
      </div>
    );
  }

  if (!projectLocationId) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Zone</Label>
        <ZonePicker
          locationId={projectLocationId}
          value={step.zone}
          onChange={(zone) => onUpdate(step.id, { zone, spot: null })}
          disabled={isPending}
          placeholder="Select a zone"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Spot</Label>
        <SpotPicker
          locationId={projectLocationId}
          zoneId={step.zone}
          value={step.spot}
          onChange={(spot) => onUpdate(step.id, { spot })}
          disabled={isPending}
          placeholder="Select a spot"
        />
      </div>
    </div>
  );
}

interface StepCardProps {
  step: WorkStep;
  projectLocationId?: number;
  canEditLocation: boolean;
  onUpdate: (stepId: number, data: { result?: WorkStep['result']; notes?: string; zone?: number | null; spot?: number | null }) => void;
  isPending: boolean;
}

function StepCard({ step, projectLocationId, canEditLocation, onUpdate, isPending }: StepCardProps) {
  const timeZone = useOrgTimeZone();
  const completed = COMPLETED_RESULTS.has(step.result);

  const resultButton = (value: WorkStep['result'], label: string, variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'outline') => (
    <Button
      key={value}
      type="button"
      variant={step.result === value ? variant : 'outline'}
      size="lg"
      className="h-14 sm:h-11 text-base sm:text-sm w-full"
      onClick={() => onUpdate(step.id, { result: value })}
      disabled={isPending}
    >
      {label}
    </Button>
  );

  const actionButtons =
    step.result_mode === 'pass_fail'
      ? [
          resultButton('pass', 'Pass', 'default'),
          resultButton('fail', 'Fail', 'destructive'),
          resultButton('n_a', 'N/A'),
        ]
      : [resultButton('done', 'Done', 'default'), resultButton('n_a', 'N/A')];

  return (
    <Card className="overflow-hidden" data-testid={`step-card-${step.id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500 dark:text-muted-foreground tabular-nums">#{step.order}</span>
                <h3 className="font-semibold text-gray-900 dark:text-foreground text-base sm:text-lg leading-tight">
                  {step.title}
                </h3>
              </div>
              {completed && step.completed_by_username && step.completed_at && (
                <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                  Completed by {step.completed_by_username} at{' '}
                  {formatDateTimeInTimeZone(step.completed_at, timeZone)}
                </p>
              )}
            </div>
            <div className="shrink-0">{resultBadge(step.result)}</div>
          </div>

          {completed ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="h-14 sm:h-11 text-base sm:text-sm w-full sm:w-auto"
                onClick={() => onUpdate(step.id, { result: 'pending' })}
                disabled={isPending}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reopen
              </Button>
              <span className="text-sm text-gray-500 dark:text-muted-foreground">
                Reopening clears completion metadata.
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {actionButtons}
            </div>
          )}

          <StepLocationPickers
            step={step}
            projectLocationId={projectLocationId}
            canEditLocation={canEditLocation}
            onUpdate={onUpdate}
            isPending={isPending}
          />
          <StepNotes
            step={step}
            onSave={(notes) => onUpdate(step.id, { notes })}
            isPending={isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function WorkTaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : 0;
  const { hasMinRole, canAdminWork } = usePermissions();
  const canManage = hasMinRole('Manager');
  const canEditStepLocation = canAdminWork;

  const { data: task, isLoading: taskLoading, isError: taskError } = useWorkTask(numericId);
  const {
    data: steps = [],
    isLoading: stepsLoading,
    isError: stepsError,
  } = useWorkSteps(numericId);
  const { data: project } = useProject(task?.project ?? 0);
  const updateStep = useUpdateWorkStep(numericId);
  const updateTask = useUpdateWorkTask();

  const projectLocationId = project?.location_fk ?? undefined;

  const projectName = useMemo(() => {
    if (!task) return '';
    return project ? `${project.project_number} — ${project.name}` : `Project ${task.project}`;
  }, [task, project]);

  const progressPercent = Math.round((task?.progress ?? 0) * 100);
  const completedCount = steps.filter((s) => COMPLETED_RESULTS.has(s.result)).length;
  const failedCount = steps.filter((s) => s.result === 'fail').length;
  const totalCount = steps.length;

  const handleStepUpdate = (
    stepId: number,
    data: { result?: WorkStep['result']; notes?: string; zone?: number | null; spot?: number | null }
  ) => {
    updateStep.mutate({ stepId, data });
  };

  const handleStatusChange = (status: WorkTask['status']) => {
    updateTask.mutate({ id: numericId, data: { status } });
  };

  if (taskLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (taskError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-red-600 dark:text-red-400">Failed to load task.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/work/tasks">Back to tasks</Link>
        </Button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-gray-600 dark:text-muted-foreground">Task not found.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/work/tasks">Back to tasks</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="icon" asChild className="shrink-0">
            <Link to="/work/tasks">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground break-words">
              {task.title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1 truncate">{projectName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{taskStatusLabel(task.status)}</Badge>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusChange('in_progress')}>
                  <Play className="h-4 w-4 mr-2" />
                  Mark in progress
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('completed')}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark complete
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('blocked')}>
                  <ShieldX className="h-4 w-4 mr-2" />
                  Mark blocked
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('todo')}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to todo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <p className="text-sm text-gray-600 dark:text-muted-foreground">Progress</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">
                {progressPercent}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-muted-foreground">
                {completedCount} of {totalCount} steps completed
              </p>
              {failedCount > 0 && (
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                  {failedCount} failed
                </p>
              )}
            </div>
          </div>
          <Progress value={progressPercent} className="h-3 sm:h-4" />
          {task.assigned_to_name && (
            <p className="text-sm text-gray-600 dark:text-muted-foreground">Assigned to {task.assigned_to_name}</p>
          )}
          {task.description && (
            <p className="text-sm text-gray-700 dark:text-foreground">{task.description}</p>
          )}
        </CardContent>
      </Card>

      {stepsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full" />
          ))}
        </div>
      ) : stepsError ? (
        <p className="text-red-600 dark:text-red-400">Failed to load steps.</p>
      ) : steps.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <p className="text-gray-500 dark:text-muted-foreground">No steps yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {steps.map((step) => (
            <StepCard
              key={step.id}
              step={step}
              projectLocationId={projectLocationId}
              canEditLocation={canEditStepLocation}
              onUpdate={handleStepUpdate}
              isPending={updateStep.isPending && updateStep.variables?.stepId === step.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
